import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { connect } from 'react-redux';
import { userLoginSuccess } from 'redux/actions/login';
import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { IStonlyData } from 'types/StonlyData';
import { ITenant } from 'types/globals';
import { IUser } from 'types/User/user';
import { useLogout } from 'hooks/common/useLogout';
import { toast } from 'react-toastify';

interface UserContextType {
  setUser: (user: IUser | null) => void;
  user: IUser;
  logout: () => void;
  navLinkRef: React.RefObject<HTMLDivElement>;
  navref: HTMLDivElement | null;
  setNavref: (ref: HTMLDivElement | null) => void;
  rawSetUser: Dispatch<SetStateAction<IUser | null>>;
  stonlyData: IStonlyData | null;
  setStonlyData: React.Dispatch<React.SetStateAction<IStonlyData | null>>;
  tenantInformation: ITenant[] | null;
  setTenantInformation: (info: ITenant[]) => void;
  currentTenant: ITenant | null;
}

export const UserContext = createContext<UserContextType | null>(null);

interface UserProviderProps {
  children: React.ReactNode;
  userLoginSuccess: (user: IUser) => void;
}

function UserProvider({ children, userLoginSuccess }: UserProviderProps) {
  const [user, rawSetUser] = useState<IUser | null>(null);
  const [stonlyData, setStonlyData] = useState(null);
  const [navref, setNavref] = useState<HTMLDivElement | null>(null);
  const [tenantInformation, setTenantInformation] = useState<ITenant[] | null>(
    null,
  );
  const [activeTenantId, setActiveTenantId] = useState(
    localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT),
  );
  const navLinkRef = React.useRef<HTMLDivElement>(null);

  const { logout: handleLogout } = useLogout();

  useEffect(() => {
    const handleStorageChange = () => {
      const storedId = localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT);
      setActiveTenantId(storedId);
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const currentTenant = useMemo(() => {
    if (!tenantInformation) return null;
    const effectiveId = activeTenantId || tenantInformation[0]?.id;
    if (!effectiveId) return null;

    const found = tenantInformation.find(
      (t) => String(t.id) === String(effectiveId),
    );

    // If we have a specific ID requested but it's not in the current list yet,
    // wait for it (return null) instead of falling back to the wrong tenant.
    if (activeTenantId && !found) return null;

    return found || tenantInformation[0];
  }, [tenantInformation, activeTenantId]);

  const setUser = useCallback(
    (user: IUser | null) => {
      if (user) {
        userLoginSuccess(user);
        rawSetUser(user);
      } else {
        rawSetUser(null);
      }
    },
    [userLoginSuccess, rawSetUser],
  );

  const logout = useCallback(() => {
    // clear cookies and local storage
    document.cookie =
      'md6_var_profile=; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=varicontest.com.au;';
    toast.success('Logged out. Redirecting to login page...');
    handleLogout();
  }, [handleLogout]);

  const contextValue = useMemo(
    () => ({
      setUser,
      user,
      logout,
      navLinkRef,
      navref,
      setNavref,
      rawSetUser,
      stonlyData,
      setStonlyData,
      tenantInformation,
      setTenantInformation,
      currentTenant,
    }),
    [
      setUser,
      user,
      logout,
      navLinkRef,
      navref,
      setNavref,
      rawSetUser,
      stonlyData,
      setStonlyData,
      tenantInformation,
      setTenantInformation,
      currentTenant,
    ],
  );

  return (
    <UserContext.Provider value={contextValue as UserContextType}>
      {children}
    </UserContext.Provider>
  );
}

export default connect(null, { userLoginSuccess })(UserProvider);
